/**
 * Sysadmin Catalog → Tools list (PR-H).
 *
 * Sysadmin authors atomic tool definitions (search-asset,
 * fetch-telemetry, etc.) here. Each tool moves through the publish
 * lifecycle: draft → published → deprecated → retired. Tenants only
 * see status='published' tools that have been explicitly assigned to
 * them via /admin/platform/tenant-tool-assignments.
 *
 * Mirrors the catalog.prompts.tsx pattern (sibling) plus the
 * argus-models row interaction (deep-link to detail). Bonus over
 * prompts: cursor pagination, status filter chips, source-kind badges.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Wrench,
  Search,
  AlertTriangle,
  Plus,
  X,
  ShieldCheck,
  Package,
  Network,
  FunctionSquare,
  Tag,
  CheckCircle2,
  PauseCircle,
  Archive,
  FileEdit,
  Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useSysadminTools,
  useCreateSysadminTool,
  type SysadminToolListItem,
  type ToolStatus,
  type ToolSourceType,
  type ToolPolicy,
  type CreateToolInput,
} from '@/lib/use-sysadmin-tools';
// PR-C-MINIMAL — shared JSON parsers; same shape as the inline
// try/catch blocks the file used to have, but now centralized so
// the sibling sysadmin/studio/tools.tsx and catalog.tools.$id.tsx
// can't drift on validation logic.
import {
  parseJsonTyped,
  tryParseJsonArrayOfStrings,
} from '@/components/tools-authoring-shared/tools-shared.helpers';

// R5 P0-1/P0-2 fix (2026-05-13): list now lives under index route ID
// `/sysadmin/catalog/tools/`; parent `catalog.tools.tsx` is a pure
// layout with <Outlet/> so detail/create children can render.
export const Route = createFileRoute('/sysadmin/catalog/tools/')({
  component: SysadminToolsPage,
});

const STATUS_FILTERS: Array<{ key: ToolStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'published', label: 'Published' },
  { key: 'deprecated', label: 'Deprecated' },
  { key: 'retired', label: 'Retired' },
];

const SOURCE_FILTERS: Array<{
  key: ToolSourceType | 'all';
  label: string;
}> = [
  { key: 'all', label: 'All sources' },
  { key: 'builtin', label: 'Built-in' },
  { key: 'mcp', label: 'MCP' },
  { key: 'openapi', label: 'OpenAPI' },
  { key: 'http', label: 'HTTP' },
  { key: 'database', label: 'Database' },
  { key: 'custom_function', label: 'Custom Function' },
];

interface ToolDraft {
  slug: string;
  displayName: string;
  description: string;
  sourceType: ToolSourceType;
  category: string;
  policy: ToolPolicy;
  sideEffects: string;
  inputSchema: string;
  outputSchema: string;
  sourceConfig: string;
  mcpServerId: string;
}

const EMPTY_DRAFT: ToolDraft = {
  slug: '',
  displayName: '',
  description: '',
  sourceType: 'custom_function',
  category: 'general',
  policy: 'auto_approve',
  sideEffects: '[]',
  inputSchema: '{}',
  outputSchema: '{}',
  sourceConfig: '{}',
  mcpServerId: '',
};

export function StatusBadge({ status }: { status: string }) {
  const map: Record<
    string,
    { label: string; cls: string; Icon: typeof CheckCircle2 }
  > = {
    draft: {
      label: 'Draft',
      cls: 'bg-slate-500/10 text-slate-700 dark:text-slate-300',
      Icon: FileEdit,
    },
    published: {
      label: 'Published',
      cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
      Icon: CheckCircle2,
    },
    deprecated: {
      label: 'Deprecated',
      cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
      Icon: PauseCircle,
    },
    retired: {
      label: 'Retired',
      cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
      Icon: Archive,
    },
  };
  const m = map[status] ?? map.draft;
  const Icon = m.Icon;
  return (
    <Badge
      variant="default"
      className={`text-[10px] gap-1 hover:opacity-90 ${m.cls}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {m.label}
    </Badge>
  );
}

export function SourceKindBadge({ sourceType }: { sourceType: string }) {
  const map: Record<
    string,
    { label: string; Icon: typeof Wrench; cls: string }
  > = {
    builtin: {
      label: 'Native',
      Icon: Package,
      cls: 'bg-violet-500/10 text-violet-700 dark:text-violet-400',
    },
    mcp: {
      label: 'MCP',
      Icon: Network,
      cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
    },
    openapi: {
      label: 'OpenAPI',
      Icon: FunctionSquare,
      cls: 'bg-teal-500/10 text-teal-700 dark:text-teal-400',
    },
    http: {
      label: 'HTTP',
      Icon: FunctionSquare,
      cls: 'bg-teal-500/10 text-teal-700 dark:text-teal-400',
    },
    database: {
      label: 'Database',
      Icon: FunctionSquare,
      cls: 'bg-teal-500/10 text-teal-700 dark:text-teal-400',
    },
    custom_function: {
      label: 'Function',
      Icon: FunctionSquare,
      cls: 'bg-teal-500/10 text-teal-700 dark:text-teal-400',
    },
  };
  const m = map[sourceType] ?? map.custom_function;
  const Icon = m.Icon;
  return (
    <Badge
      variant="outline"
      className={`text-[10px] gap-1 border-transparent ${m.cls}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {m.label}
    </Badge>
  );
}

function SysadminToolsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<ToolStatus | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<ToolSourceType | 'all'>(
    'all',
  );
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useSysadminTools({
    status: statusFilter === 'all' ? undefined : statusFilter,
    sourceType: sourceFilter === 'all' ? undefined : sourceFilter,
    search: search.trim() || undefined,
    limit: 50,
  });

  const create = useCreateSysadminTool();

  const [draft, setDraft] = useState<ToolDraft | null>(null);

  const items = useMemo(() => data?.items ?? [], [data]);

  const handleCreate = async () => {
    if (!draft) return;
    // PR-C-MINIMAL — replace 4 inline try/catch JSON blocks with the
    // shared helpers. Keeps the surface-level toast UX identical
    // (one toast per failure, no behaviour change).
    const inputP = parseJsonTyped(draft.inputSchema, 'Input schema');
    if (!inputP.ok) {
      toast.error('Input schema must be valid JSON');
      return;
    }
    const outputP = parseJsonTyped(draft.outputSchema, 'Output schema');
    if (!outputP.ok) {
      toast.error('Output schema must be valid JSON');
      return;
    }
    const sourceCfgP = parseJsonTyped(draft.sourceConfig, 'Source config');
    if (!sourceCfgP.ok) {
      toast.error('Source config must be valid JSON');
      return;
    }
    const sideEffectsP = tryParseJsonArrayOfStrings(
      draft.sideEffects,
      'Side effects',
    );
    if (!sideEffectsP.ok) {
      toast.error('Side effects must be a JSON array of strings', {
        description: sideEffectsP.err,
      });
      return;
    }

    const payload: CreateToolInput = {
      slug: draft.slug,
      displayName: draft.displayName,
      description: draft.description,
      sourceType: draft.sourceType,
      sourceConfig: sourceCfgP.value as Record<string, unknown>,
      inputSchema: inputP.value as Record<string, unknown>,
      outputSchema: outputP.value as Record<string, unknown>,
      policy: draft.policy,
      sideEffects: sideEffectsP.value,
      category: draft.category,
      mcpServerId: draft.mcpServerId.trim() || null,
    };

    try {
      const result = await create.mutateAsync(payload);
      toast.success(`Created "${result.data.slug}" (status: draft)`);
      setDraft(null);
    } catch (err) {
      toast.error('Create failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Wrench className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Tools Catalog
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">
              §3.3
            </Badge>
            <Badge variant="secondary" className="text-xs">
              v1
            </Badge>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">System Tools</CardTitle>
          <CardDescription>
            Atomic capability primitives. Implement via MCP server, built-in
            wrapper function, or native handler. Tenants only see published
            tools that have been explicitly assigned to them via the Tenant
            Detail → Asset Assignments tab.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search slug or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
            aria-label="Search tools"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as ToolStatus | 'all')}
        >
          <SelectTrigger className="w-40 h-9" aria-label="Status filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.key} value={f.key}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={sourceFilter}
          onValueChange={(v) => setSourceFilter(v as ToolSourceType | 'all')}
        >
          <SelectTrigger className="w-44 h-9" aria-label="Source kind filter">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_FILTERS.map((f) => (
              <SelectItem key={f.key} value={f.key}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          {/* Phase 1 — secondary quick-draft via Sheet drawer with raw
              JSON Schema textareas. Primary entry is now the wizard
              below (with Tool Helper + rich parameter editor).
              Sheet remains for power users who want to paste JSON. */}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setDraft({ ...EMPTY_DRAFT })}
            data-testid="new-tool-quick-button"
            title="Quick draft — paste JSON schemas directly. Use the primary New Tool button for the guided wizard."
          >
            <FileEdit className="h-3.5 w-3.5" /> Quick JSON draft
          </Button>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() =>
              navigate({ to: '/sysadmin/catalog/tools/new' })
            }
            data-testid="new-tool-button"
          >
            <Plus className="h-3.5 w-3.5" /> New Tool
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading tools…
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {search || statusFilter !== 'all' || sourceFilter !== 'all'
              ? 'No tools match these filters.'
              : 'No tools yet. Create your first tool with the button above.'}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-56">Slug / Name</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-28">Source</TableHead>
                  <TableHead className="w-28">Category</TableHead>
                  <TableHead className="w-20">Version</TableHead>
                  <TableHead className="w-24" title="Number of tenants">
                    Assigned
                  </TableHead>
                  <TableHead className="w-44">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((t) => (
                  <ToolRow key={t.id} tool={t} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Sheet
        open={draft !== null}
        onOpenChange={(open) => !open && setDraft(null)}
      >
        <SheetContent
          side="right"
          className="sm:max-w-2xl overflow-y-auto"
          data-testid="new-tool-drawer"
        >
          <SheetHeader>
            <SheetTitle>New tool</SheetTitle>
            <SheetDescription>
              Tools are created in draft. Once happy, click Publish in the
              detail page to make it available for tenant assignment.
            </SheetDescription>
          </SheetHeader>
          {draft && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="t-slug" className="text-xs">
                    Slug (immutable)
                  </Label>
                  <Input
                    id="t-slug"
                    value={draft.slug}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        slug: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9_.-]/g, '-'),
                      })
                    }
                    placeholder="search-asset"
                    className="font-mono text-xs h-8"
                    data-testid="tool-slug-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-category" className="text-xs">
                    Category
                  </Label>
                  <Input
                    id="t-category"
                    value={draft.category}
                    onChange={(e) =>
                      setDraft({ ...draft, category: e.target.value })
                    }
                    placeholder="assets"
                    className="text-xs h-8"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="t-name" className="text-xs">
                  Display name
                </Label>
                <Input
                  id="t-name"
                  value={draft.displayName}
                  onChange={(e) =>
                    setDraft({ ...draft, displayName: e.target.value })
                  }
                  placeholder="Search asset registry"
                  className="text-sm h-8"
                  data-testid="tool-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="t-desc" className="text-xs">
                  Description
                </Label>
                <Textarea
                  id="t-desc"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft({ ...draft, description: e.target.value })
                  }
                  placeholder="Look up an asset by tag or partial match."
                  rows={2}
                  className="text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Source type</Label>
                  <Select
                    value={draft.sourceType}
                    onValueChange={(v) =>
                      setDraft({ ...draft, sourceType: v as ToolSourceType })
                    }
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="builtin">Built-in (native)</SelectItem>
                      <SelectItem value="mcp">MCP server</SelectItem>
                      <SelectItem value="openapi">OpenAPI</SelectItem>
                      <SelectItem value="http">HTTP</SelectItem>
                      <SelectItem value="database">Database</SelectItem>
                      <SelectItem value="custom_function">
                        Custom function
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Policy</Label>
                  <Select
                    value={draft.policy}
                    onValueChange={(v) =>
                      setDraft({ ...draft, policy: v as ToolPolicy })
                    }
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto_approve">Auto-approve</SelectItem>
                      <SelectItem value="require_approval">
                        Require approval
                      </SelectItem>
                      <SelectItem value="forbidden">Forbidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {draft.sourceType === 'mcp' && (
                <div className="space-y-1.5">
                  <Label
                    htmlFor="t-mcp"
                    className="text-xs flex items-center gap-1.5"
                  >
                    <Link2 className="h-3 w-3" /> MCP server ID
                  </Label>
                  <Input
                    id="t-mcp"
                    value={draft.mcpServerId}
                    onChange={(e) =>
                      setDraft({ ...draft, mcpServerId: e.target.value })
                    }
                    placeholder="(populated from MCP catalog when PR-F lands)"
                    className="font-mono text-xs h-8"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="t-side" className="text-xs">
                  Side effects (JSON array of strings)
                </Label>
                <Input
                  id="t-side"
                  value={draft.sideEffects}
                  onChange={(e) =>
                    setDraft({ ...draft, sideEffects: e.target.value })
                  }
                  placeholder='["read"]'
                  className="font-mono text-xs h-8"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="t-input" className="text-xs">
                    Input schema (JSON)
                  </Label>
                  <Textarea
                    id="t-input"
                    value={draft.inputSchema}
                    onChange={(e) =>
                      setDraft({ ...draft, inputSchema: e.target.value })
                    }
                    placeholder='{"type":"object","properties":{...}}'
                    rows={6}
                    className="text-xs font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-output" className="text-xs">
                    Output schema (JSON)
                  </Label>
                  <Textarea
                    id="t-output"
                    value={draft.outputSchema}
                    onChange={(e) =>
                      setDraft({ ...draft, outputSchema: e.target.value })
                    }
                    placeholder='{"type":"object","properties":{...}}'
                    rows={6}
                    className="text-xs font-mono"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="t-cfg" className="text-xs">
                  Source config (JSON, sysadmin-only — never returned to tenants)
                </Label>
                <Textarea
                  id="t-cfg"
                  value={draft.sourceConfig}
                  onChange={(e) =>
                    setDraft({ ...draft, sourceConfig: e.target.value })
                  }
                  placeholder='{"endpoint":"...","credentialRef":"..."}'
                  rows={4}
                  className="text-xs font-mono"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDraft(null)}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  data-testid="tool-create-submit"
                  disabled={
                    !draft.slug ||
                    !draft.displayName ||
                    !draft.description ||
                    create.isPending
                  }
                >
                  {create.isPending ? 'Creating…' : 'Create draft'}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ToolRow({ tool }: { tool: SysadminToolListItem }) {
  return (
    <TableRow
      className="hover:bg-muted/40"
      data-testid={`tool-row-${tool.slug}`}
    >
      <TableCell className="font-mono text-xs">
        <Link
          to="/sysadmin/catalog/tools/$id"
          params={{ id: tool.id }}
          className="font-medium hover:text-primary block"
        >
          {tool.slug}
        </Link>
        <div className="text-[11px] text-muted-foreground truncate max-w-md">
          {tool.displayName}
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge status={tool.status} />
      </TableCell>
      <TableCell>
        <SourceKindBadge sourceType={tool.sourceType} />
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-[10px] gap-1">
          <Tag className="h-2.5 w-2.5" />
          {tool.category}
        </Badge>
      </TableCell>
      <TableCell className="text-xs tabular-nums">v{tool.version}</TableCell>
      <TableCell className="text-xs tabular-nums">
        {tool.assignedTenantCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-foreground">
            <ShieldCheck className="h-3 w-3 text-emerald-600" />
            {tool.assignedTenantCount}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground tabular-nums">
        {new Date(tool.updatedAt).toLocaleString()}
      </TableCell>
    </TableRow>
  );
}
